// Ferramenta de diagnóstico: busca em TODAS as fontes possíveis pra achar
// pedidos "sumidos" — inclui Bling histórico, cancelados, ocultos e AJ.
//
// Útil quando um pedido conhecido não aparece em Pedidos/Logística/Kanban.

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getPedidos,
  getBlingHistorico,
  getBlingContatos,
  getBlingOcultos,
  getBlingStatusOverrides,
  hideBlingPedido,
  unhideBlingPedido,
} from '../utils/storage'
import { Search, ExternalLink, EyeOff, Eye, AlertCircle, MapPin } from 'lucide-react'
import { detectarCidadeAba, CIDADES_ABAS } from '../data/kanban'

function norm(s) {
  return (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// Mapeia situação Bling pra label legível
const SITUACAO_BLING = {
  0: { label: 'Rascunho', cor: 'bg-slate-100 text-slate-600' },
  6: { label: 'Aberto',   cor: 'bg-blue-100 text-blue-700' },
  9: { label: 'Atendido', cor: 'bg-emerald-100 text-emerald-700' },
  12:{ label: 'Cancelado',cor: 'bg-red-100 text-red-700' },
}

export function Diagnostico() {
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')

  const contatosMap = useMemo(
    () => new Map(getBlingContatos().map(c => [String(c.id), c])),
    []
  )
  const ocultos    = useMemo(() => getBlingOcultos(), [])
  const overrides  = useMemo(() => getBlingStatusOverrides(), [])

  // Snapshot de TUDO
  const fontes = useMemo(() => ({
    locais:        getPedidos(),
    blingTotal:    getBlingHistorico(),
    ocultos,
    overrides,
  }), [ocultos, overrides])

  // Set de números cadastrados localmente — usado pra detectar quando um pedido
  // Bling tem o mesmo número de um local (a dedup do useOrders esconde o Bling
  // nesse caso, mostrando só o local — pode dar a impressão que "sumiu").
  const numerosLocais = useMemo(
    () => new Set(fontes.locais.map(p => String(p.numero))),
    [fontes.locais]
  )

  // Resultados da busca
  const resultados = useMemo(() => {
    const termos = norm(busca).split(/\s+/).filter(Boolean)
    if (termos.length === 0) return null
    const matchTermos = (corpus) => termos.every(t => corpus.includes(t))

    const matchPedido = (p, fonte) => {
      const c = p.cliente || p.contato || {}
      const corpus = norm([
        p.numero, p.numeroLoja,
        c.nome, c.numeroDocumento,
        c.celular, c.telefone, c.contato,
        c.endereco?.geral?.endereco, c.endereco?.geral?.municipio,
        c.endereco, c.cidade, c.bairro,
        p.resumo,
      ].filter(Boolean).join(' | '))
      // Match também por dígitos puros de telefone
      const digitos = (busca.match(/\d+/g) || []).join('')
      const digitosCorpus = (
        (c.celular || '') + (c.telefone || '') + (c.contato || '') +
        (c.numeroDocumento || '') + (p.numero || '')
      ).replace(/\D/g, '')
      const matchDig = digitos && digitos.length >= 4 && digitosCorpus.includes(digitos)
      return matchTermos(corpus) || matchDig
    }

    return {
      locais:     fontes.locais.filter(p => matchPedido(p, 'local')),
      bling:      fontes.blingTotal.filter(p => matchPedido(p, 'bling')),
    }
  }, [busca, fontes])

  function copiarDiagnostico(p, contexto) {
    // Pra pedido Bling, reconstrói endereço pelo contatosMap (igual o sistema faz)
    let enderecoCompleto = null
    let abaDetectada = null
    if (contexto === 'bling') {
      const cont = contatosMap.get(String(p.contato?.id || '')) || {}
      const end  = cont.endereco?.geral || {}
      enderecoCompleto = [end.endereco, end.numero, end.complemento, end.bairro, end.municipio, end.uf]
        .filter(Boolean).join(', ') || null
      abaDetectada = detectarCidadeAba({
        cliente: { cidade: end.municipio, bairro: end.bairro, endereco: enderecoCompleto }
      })
    } else {
      enderecoCompleto = p.cliente?.endereco || null
      abaDetectada = detectarCidadeAba(p)
    }
    const txt = JSON.stringify({
      contexto,
      id: p.id,
      numero: p.numero || p.numeroLoja,
      numeroBling: p.numero,
      cliente: p.cliente?.nome || p.contato?.nome,
      situacao: p.situacao,
      oculto: ocultos.has(`bling-${p.id}`),
      status: p.status,
      prazo: p.prazo || p.dataPrevista,
      endereco: enderecoCompleto,
      abaDetectada,
    }, null, 2)
    navigator.clipboard.writeText(txt)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <AlertCircle size={20} className="text-amber-600" />
          Diagnóstico de pedido sumido
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Busca em <strong>todas as fontes</strong>: locais, Bling (incluindo cancelados/ocultos),
          e mostra onde cada pedido está e por que pode não estar aparecendo nas outras telas.
        </p>
      </div>

      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          type="text"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Ex: Fatima Smith / 0042 / 84988887777 / CNPJ"
          className="input w-full pl-9 text-sm py-3"
        />
        {busca && (
          <p className="text-[10px] text-slate-400 mt-1.5">
            Busca ignora acentos · múltiplas palavras em AND · dígitos batem telefone/CPF/número.
          </p>
        )}
      </div>

      {/* Resumo das fontes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-xs">
        <Stat label="Pedidos locais" valor={fontes.locais.length} />
        <Stat label="Bling histórico" valor={fontes.blingTotal.length} />
        <Stat label="Ocultos no Bling" valor={fontes.ocultos.size} alerta={fontes.ocultos.size > 0} />
        <Stat label="Status sobrescrito" valor={Object.keys(fontes.overrides).length} />
      </div>

      {/* Resultados */}
      {resultados ? (
        <div className="space-y-4">
          <ResultadoSecao
            titulo="📋 Pedidos locais (cadastrados manualmente)"
            itens={resultados.locais}
            renderItem={p => (
              <PedidoLocal key={p.id} p={p} onAbrir={() => navigate(`/pedidos/${p.id}`)} onCopiar={() => copiarDiagnostico(p, 'local')} />
            )}
            vazioMsg="Nenhum pedido local bate com a busca"
          />

          <ResultadoSecao
            titulo="🔗 Pedidos do Bling (TODOS, incluindo cancelados e ocultos)"
            itens={resultados.bling}
            renderItem={p => (
              <PedidoBling
                key={p.id}
                p={p}
                ocultos={ocultos}
                overrides={overrides}
                contatosMap={contatosMap}
                numerosLocais={numerosLocais}
                onAbrir={() => navigate(`/pedidos/bling-${p.id}`)}
                onCopiar={() => copiarDiagnostico(p, 'bling')}
                onDesocultar={() => {
                  if (window.confirm(`Tornar visível o pedido ${p.numeroLoja || p.numero}?`)) {
                    unhideBlingPedido(`bling-${p.id}`)
                    window.location.reload()
                  }
                }}
              />
            )}
            vazioMsg="Nenhum pedido do Bling bate com a busca"
          />
        </div>
      ) : (
        <p className="text-center text-sm text-slate-400 py-12 italic">
          Digite acima pra buscar
        </p>
      )}
    </div>
  )
}

function Stat({ label, valor, alerta }) {
  return (
    <div className={`p-3 rounded-xl border ${alerta ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</p>
      <p className={`text-2xl font-black ${alerta ? 'text-amber-700' : 'text-slate-800'}`}>{valor}</p>
    </div>
  )
}

function ResultadoSecao({ titulo, itens, renderItem, vazioMsg }) {
  return (
    <div className="card p-4">
      <h2 className="text-sm font-bold text-slate-700 mb-3">
        {titulo} <span className="text-slate-400 font-normal">({itens.length})</span>
      </h2>
      {itens.length === 0 ? (
        <p className="text-xs text-slate-400 italic">{vazioMsg}</p>
      ) : (
        <div className="space-y-2">{itens.map(renderItem)}</div>
      )}
    </div>
  )
}

function PedidoLocal({ p, onAbrir, onCopiar }) {
  const abaDetectada = detectarCidadeAba(p)
  const labelAba     = CIDADES_ABAS.find(a => a.id === abaDetectada)?.label || abaDetectada
  // Diagnósticos: porque o pedido local pode não aparecer na Logística
  const motivosNaoAparece = []
  if (!p.prazo || p.prazo === '0000-00-00') motivosNaoAparece.push('Sem prazo — não aparece em Logística (só Pedidos)')
  else if (!p.prazo.startsWith('2026')) motivosNaoAparece.push(`Prazo ${p.prazo} — Logística mostra só 2026`)
  if (p.dividido) motivosNaoAparece.push('Pedido foi DIVIDIDO em filhos — pai sai da Logística, só filhos aparecem')
  if (abaDetectada === 'OUTRAS') motivosNaoAparece.push('Cidade não classificada — cai em "Outras", não aparece nas abas JP/Recife/CG/Natal')
  if (p.status === 'ENTREGUE' || p.status === 'POS_VENDA') {
    motivosNaoAparece.push(
      `Status "${p.status}" — Logística esconde por padrão (ligue "Entregues" pra ver, ou abra o pedido e troque o status pra CADASTRADO/FABRICANDO).`
    )
  }
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40 hover:bg-slate-50 transition">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-mono font-bold text-slate-700">{p.numero}</span>
        <span className="text-[10px] uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-bold">LOCAL</span>
        {p.status && (
          <span className="text-[10px] uppercase bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-bold">{p.status}</span>
        )}
        {p.tipoAjuste && (
          <span className="text-[10px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold">AJUSTE</span>
        )}
        {p.tipoFilho && (
          <span className="text-[10px] uppercase bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-bold">FILHO</span>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-800">{p.cliente?.nome || '—'}</p>
      <p className="text-xs text-slate-500 mt-0.5">
        {p.cliente?.contato} · Prazo: {p.prazo || '—'}
      </p>
      {/* Endereço + aba detectada (igual ao card Bling) */}
      <div className="mt-2 p-2 bg-white/60 rounded border border-slate-200/60 text-[11px]">
        <div className="flex items-start gap-1.5">
          <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-slate-700">
              {p.cliente?.endereco || <span className="italic text-slate-400">sem endereço cadastrado</span>}
            </p>
            <p className="mt-1 text-[10px] flex items-center gap-1">
              <span className="text-slate-400">Sistema detecta aba:</span>
              <span className={`font-bold px-1.5 py-0.5 rounded ${
                abaDetectada === 'OUTRAS'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-800'
              }`}>
                {labelAba}
              </span>
            </p>
          </div>
        </div>
      </div>
      {motivosNaoAparece.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] space-y-0.5">
          <p className="font-bold uppercase text-[9px] tracking-wide text-amber-700 mb-1">⚠️ Por que pode não aparecer:</p>
          {motivosNaoAparece.map((m, i) => (
            <p key={i} className="text-slate-700">• {m}</p>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button onClick={onAbrir} className="text-xs font-bold text-[#CC8800] hover:underline flex items-center gap-1">
          <ExternalLink size={11} /> Abrir
        </button>
        <button onClick={onCopiar} className="text-[10px] text-slate-400 hover:text-slate-600">
          Copiar JSON
        </button>
      </div>
    </div>
  )
}

function PedidoBling({ p, ocultos, overrides, contatosMap, numerosLocais, onAbrir, onCopiar, onDesocultar }) {
  const idCompleto = `bling-${p.id}`
  const oculto     = ocultos.has(idCompleto)
  const sit        = SITUACAO_BLING[p.situacao?.id] || { label: `Sit ${p.situacao?.id}`, cor: 'bg-slate-100 text-slate-600' }
  const statusOver = overrides[idCompleto]
  // Conflito de dedup: existe pedido LOCAL com mesmo numeroLoja → useOrders
  // esconde este Bling em favor do local (causa frequente de "sumiu")
  const numeroLoja = String(p.numeroLoja || '')
  const duplicadoLocal = numeroLoja && numerosLocais?.has(numeroLoja)

  // Reconstrói o pedido normalizado igual ao getPedidosBlingAtivos
  // pra mostrar endereço e detectar a aba de cidade
  const cont    = contatosMap.get(String(p.contato?.id || '')) || {}
  const end     = cont.endereco?.geral || {}
  const enderecoCompleto = [end.endereco, end.numero, end.complemento, end.bairro, end.municipio, end.uf]
    .filter(Boolean).join(', ')
  const pedidoNormalizado = {
    cliente: {
      cidade:   end.municipio || '',
      bairro:   end.bairro || '',
      endereco: enderecoCompleto,
    },
  }
  const abaDetectada = detectarCidadeAba(pedidoNormalizado)
  const labelAba = CIDADES_ABAS.find(a => a.id === abaDetectada)?.label || abaDetectada

  // Diagnósticos
  const motivosNaoAparece = []
  if (duplicadoLocal) motivosNaoAparece.push(
    `Existe pedido LOCAL com mesmo número (${numeroLoja}) — só o LOCAL aparece em Logística/Pedidos (Bling fica oculto pela dedup). Procure acima na seção "Pedidos locais" pra ver os dados que estão em uso.`
  )
  if (p.situacao?.id === 12) motivosNaoAparece.push('Cancelado no Bling — filtrado de Logística e Kanban')
  if (p.situacao?.id === 0)  motivosNaoAparece.push('Rascunho no Bling — filtrado')
  if (oculto)                motivosNaoAparece.push('Foi marcado como oculto manualmente')
  if (statusOver === 'ENTREGUE' || statusOver === 'POS_VENDA') {
    motivosNaoAparece.push(
      `Status sobrescrito como "${statusOver}" no MINI Deck — Logística esconde por padrão (ligue "Entregues" pra ver, ou abra o pedido e troque o status pra CADASTRADO/FABRICANDO).`
    )
  }
  if (!p.dataPrevista || p.dataPrevista === '0000-00-00') motivosNaoAparece.push('Sem prazo (não aparece em Logística)')
  else if (!p.dataPrevista.startsWith('2026')) motivosNaoAparece.push(`Prazo ${p.dataPrevista} — Logística mostra só 2026`)
  if (abaDetectada === 'OUTRAS' && enderecoCompleto) {
    motivosNaoAparece.push(`Endereço não bate com nenhuma aba conhecida — cai em "Outras", não JP/Recife/CG/Natal`)
  }
  if (!enderecoCompleto) {
    motivosNaoAparece.push('Cliente sem endereço — sistema não consegue classificar em nenhuma cidade')
  }

  return (
    <div className={`border rounded-lg p-3 transition ${
      oculto ? 'border-amber-300 bg-amber-50/60' :
      p.situacao?.id === 12 ? 'border-red-200 bg-red-50/40' :
      'border-blue-200 bg-blue-50/30 hover:bg-blue-50'
    }`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-mono font-bold text-slate-700">{p.numeroLoja || `BL-${p.numero}`}</span>
        <span className="text-[10px] uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">BLING #{p.numero}</span>
        <span className={`text-[10px] uppercase ${sit.cor} px-2 py-0.5 rounded font-bold`}>{sit.label}</span>
        {oculto && (
          <span className="text-[10px] uppercase bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-bold flex items-center gap-1">
            <EyeOff size={9} /> OCULTO
          </span>
        )}
        {statusOver && (
          <span className="text-[10px] uppercase bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-bold">→ {statusOver}</span>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-800">{p.contato?.nome || '—'}</p>
      <p className="text-xs text-slate-500 mt-0.5">
        {p.contato?.celular || p.contato?.telefone} · Venda: {p.data || '—'} · Prazo: {p.dataPrevista || '—'}
      </p>

      {/* Endereço + aba detectada */}
      <div className="mt-2 p-2 bg-white/60 rounded border border-slate-200/60 text-[11px]">
        <div className="flex items-start gap-1.5">
          <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-slate-700">
              {enderecoCompleto || <span className="italic text-slate-400">sem endereço cadastrado no cliente</span>}
            </p>
            <p className="mt-1 text-[10px] flex items-center gap-1">
              <span className="text-slate-400">Sistema detecta aba:</span>
              <span className={`font-bold px-1.5 py-0.5 rounded ${
                abaDetectada === 'OUTRAS'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-800'
              }`}>
                {labelAba}
              </span>
            </p>
          </div>
        </div>
      </div>

      {motivosNaoAparece.length > 0 && (
        <div className="mt-2 pt-2 border-t border-current/10 text-[11px] space-y-0.5">
          <p className="font-bold uppercase text-[9px] tracking-wide text-amber-700 mb-1">⚠️ Por que pode não aparecer:</p>
          {motivosNaoAparece.map((m, i) => (
            <p key={i} className="text-slate-700">• {m}</p>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <button onClick={onAbrir} className="text-xs font-bold text-[#CC8800] hover:underline flex items-center gap-1">
          <ExternalLink size={11} /> Abrir detalhe
        </button>
        {oculto && (
          <button onClick={onDesocultar} className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1">
            <Eye size={11} /> Desocultar
          </button>
        )}
        <button onClick={onCopiar} className="text-[10px] text-slate-400 hover:text-slate-600 ml-auto">
          Copiar JSON
        </button>
      </div>
    </div>
  )
}
